import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { AfterViewInit, ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { InsightsService } from '@services/insights.service';
import { SessionStorageService } from '@services/storage.service';
import { AuditService } from '@shared/services/audit.service';
import { EmbedconfigService } from '@shared/services/embedconfig.service';
import { ExternalUrlService } from '@shared/services/externalurl.service';
import { MenuService } from '@shared/services/menu.service';
import { IReport, ReportService } from '@shared/services/report.service';
import { SidenavService } from '@shared/services/sidenav.service';
import { IUserAccess, UserService } from '@shared/services/user.service';
import { SidebarItem } from '@shared/sidebaritem';
import { combineLatest, debounceTime, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ListItemComponent } from '../menu-list-item/list-item/list-item.component';
import { IData, MenuListItemComponent } from '../menu-list-item/menu-list-item.component';
import { sidebarItems } from './menu';

@Component({
	selector: 'app-insights',
	templateUrl: './insights.component.html',
	styleUrls: ['./insights.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class InsightsComponent implements OnInit, AfterViewInit, OnDestroy {
	theme: string = 'my-light-theme';
	isExpanded = true;
	element: HTMLElement;
	permissions: string[];
	isReportSelected = false;
	sidebarItems: SidebarItem[] = sidebarItems;

	public filter = new UntypedFormControl('');
	public data$: Observable<IData[]>;
	public dataFiltered$: Observable<IData[]>;
	impersonateUser: IUserAccess;
	noReports = true;
	@ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;
	private destroyNotifier$ = new Subject<void>();
	selected = '';

	@ViewChild('child') private child: MenuListItemComponent;

	private rightSidebarContent: TemplateRef<any>;
	@ViewChild('rightSidebarContent')
	set content(content: TemplateRef<any>) {
		this.rightSidebarContent = content;
	}

	get content(): TemplateRef<any> {
		return this.rightSidebarContent;
	}

	public config = {
		paddingAtStart: true,
		interfaceWithRoute: true,
		highlightOnSelect: true,
		collapseOnSelect: false,
		useDividers: false,
	};

	private keyManager: ActiveDescendantKeyManager<ListItemComponent>;

	constructor(
		private insightsService: InsightsService,
		private sidenavService: SidenavService,
		// private keyManager: ActiveDescendantKeyManager<ListItemComponent>,
		private menuService: MenuService,
		private reportService: ReportService,
		private externalUrlService: ExternalUrlService,
		private router: Router,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
		private auditService: AuditService,
		private embedConfigService: EmbedconfigService,
	) {
		this.data$ = this.reportService.loadReportCategories().pipe(
			map((categories) => categories.sort((a, b) => (a.sortOrder >= b.sortOrder ? -1 : 1))),
			switchMap((categories) =>
				this.reportService.reports$.pipe(
					map((reports) => {
						return reports.reduce((accumulated, current) => {
							current.categoryName = current.categoryId && categories.find((category) => category.id === current.categoryId) ? categories.find((category) => category.id === current.categoryId).name : '';
							current.categorySortOrder = current.categoryId && categories.find((category) => category.id === current.categoryId) ? categories.find((category) => category.id === current.categoryId).sortOrder : 999;

							accumulated.push({ report: current });
							accumulated = accumulated.sort((r1, r2) => {
								const catA = this.getCategoryName(r1, categories);
								const catB = this.getCategoryName(r2, categories);

								if (typeof r1.report.categoryId === 'undefined' || r1.report.categoryId === null || !catA) {
									return 1;
								}
								if (typeof r2.report.categoryId === 'undefined' || r2.report.categoryId === null || !catB) {
									return -1;
								}

								const nameA = r1.report.name.toUpperCase();
								const nameB = r2.report.name.toUpperCase();

								if (catA === catB) {
									if (nameA === nameB) return 0;
									return nameA < nameB ? -1 : 1;
								}

								return catA < catB ? -1 : 1;
							});
							return accumulated;
						}, [] as IData[]);
					}),
				),
			),
			map((r) => r.sort((a, b) => (a.report.categorySortOrder >= b.report.categorySortOrder ? 1 : -1))), // Sort the final report list on the sortOrder of the reportCategories (defined via drag/drop in overlay)
			// shareReplay() // Avoid triggering a http request at each keystroke in the filter - has the negative side effect of not updating the category order when it changes in the overlay
		);

		// this.dataFiltered$ = merge(this.data$, this.filter.valueChanges).pipe(
		this.dataFiltered$ = this.filter.valueChanges.pipe(
			// Should be sufficient to only listen to the valueChanges and then switchMap it into the data$ observable?
			takeUntil(this.destroyNotifier$),
			debounceTime(250),
			// distinctUntilChanged(),
			switchMap(() => this.data$),
			map((v) => {
				return v.filter((o) => o.report.name.toLowerCase().includes(this.filter.value.toLowerCase()));
			}),
		);

		this.reportService.report$.pipe(takeUntil(this.destroyNotifier$)).subscribe((v) => {
			let reportId;
			if (v) {
				reportId = v.reportId ? v.reportId : v.id;
			}
			this.selected = reportId;
			if (!this.filter.getRawValue()) this.filter.setValue('');
		});
	}

	private getCategoryName(r, c) {
		let cat = '';
		if (r && r.report.categoryId) {
			const tmpCat = c.find((category) => category.id === r.report.categoryId);
			if (tmpCat && tmpCat.name) {
				cat = tmpCat.name.toUpperCase();
			}
		}
		return cat;
	}

	ngOnInit(): void {
		const userId = this.sessionStorageService.get('User.IAM.Id');
		this.auditService
			.LogEvent({
				service: 'insights',
				resource: 'Login',
				event: 'Created',
				data: {
					id: userId,
					version: undefined,
				},
			})
			.subscribe((c) => console.log(c));
		this.sidenavService.setMenuData(this.sidebarItems);

		this.sessionStorageService
			.watch('User.Permissions')
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe((x) => (this.permissions = JSON.parse(x)));

		combineLatest([
			this.reportService.fetchUserReportsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
			this.externalUrlService.fetchUserExternalUrlsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
		])
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe((data) => {
				if (data[0] && data[1]) {
					const extUrlsReports: IReport[] = data[1].result;
					extUrlsReports.forEach((r) => {
						r.externalId = r.url;
					});
					this.reportService.userReports = [...data[0].result, ...extUrlsReports];
				}
			});

		this.userService.impersonateUser$.pipe(takeUntil(this.destroyNotifier$)).subscribe((userAccess) => {
			if (userAccess) {
				this.impersonateUser = userAccess;
				this.embedConfigService.impersonateUserId = this.impersonateUser.id;
				this.embedConfigService.impersonateUserPermissions = this.impersonateUser.permissionCodes;
				this.embedConfigService.flushAllEmbedConfigs();
				this.reportService.fetchUserReports(this.impersonateUser.id);
				this.sessionStorageService.set('App.ImpersonateUser', JSON.stringify(this.impersonateUser));
			} else {
				this.sessionStorageService.remove('App.ImpersonateUser');
				this.impersonateUser = null;
				this.embedConfigService.impersonateUserId = null;
				this.embedConfigService.impersonateUserPermissions = null;
				this.reportService.fetchReports(true);
			}
		});

		this.userService.account$.pipe(takeUntil(this.destroyNotifier$)).subscribe((acc) => {
			if (acc) {
				this.embedConfigService.flushAllEmbedConfigs();
				this.reportService.fetchUserReports(null, this.permissions.findIndex((x) => x === 'insights:ReportCreate') > 0);
				this.reportService.synchronizeWithPowerBI(
					'REPORT',
					this.permissions.findIndex((x) => x === 'insights:ReportCreate') > 0,
					false, // Added 13-03-2023 to avoid loading the entire report list twice
				);
			}
		});

		this.reportService.userReports$.pipe(takeUntil(this.destroyNotifier$)).subscribe((userReports) => {
			if (userReports?.length > 0) {
				this.noReports = false;
			} else {
				this.noReports = true;
			}
		});

		this.reportService.reports$.pipe(takeUntil(this.destroyNotifier$)).subscribe((reports) => {
			if (reports && reports.length > 0) {
				const sortedReports = reports.sort(this.compare.bind(this));
				this.insightsService
					.fetchUserById()
					.pipe(take(1))
					.subscribe((x) => {
						if (x.result?.defaultViewType === 'report' && x.result?.defaultViewId) {
							this.sessionStorageService.set('Insights.User.DefaultReport', x.result.defaultViewId);
						}
						const defaultViewId = this.impersonateUser && this.impersonateUser.defaultViewId ? this.impersonateUser.defaultViewId : this.sessionStorageService.get('Insights.User.DefaultReport');
						const defaultReport: IReport = sortedReports.find((x) => (x.url?.length > 0 ? x.id === defaultViewId : x.externalId === defaultViewId));

						const saveAsReportExternalId: string = this.sessionStorageService.get('Insights.SaveAsReportId');

						if (saveAsReportExternalId) {
							console.log('Selecting new "Saved as..." report with externalId:', saveAsReportExternalId);
							this.sessionStorageService.remove('Insights.SaveAsReportId');
							const newCoreReport = sortedReports.find((r) => r.externalId === saveAsReportExternalId);
							this.keyManager.setActiveItem(this.items.find((x) => x.getLabel() === newCoreReport.name));
							this.selectReport(newCoreReport, false);
							this.menuService.reportSelect = newCoreReport;
						} else if (defaultReport) {
							this.keyManager.setActiveItem(this.items.find((x) => x.getLabel() === defaultReport.name));
							this.menuService.reportSelect = defaultReport;

							// If logging in as someone, navigate to their default report
							this.selectReport(defaultReport, !!this.impersonateUser);
						} else {
							console.log('no default report found, so selecting first in list');

							if (sortedReports.length > 0) {
								this.keyManager.setActiveItem(this.items.find((x) => x.getLabel() === sortedReports[0].name));
								this.menuService.reportSelect = sortedReports[0];
								this.selectReport(sortedReports[0], !!this.impersonateUser);
							} else {
								this.selectReport(null);
							}
						}
						if (x.result?.slideshowInterval > 0 && !this.sessionStorageService.get('Slideshow.Activated')) {
							this.sessionStorageService.set('Slideshow.Activated', 'x');
							this.activateSlideshow();
						}
					});
			} else {
				this.noReports = true;
				this.selectReport(null);
			}
		});
	}

	compare(a, b) {
		if (a.name < b.name) {
			return -1;
		}
		if (a.name > b.name) {
			return 1;
		}
		return 0;
	}

	ngAfterViewInit() {
		this.sidenavService.setRightPanelContent(this.rightSidebarContent);
		if (!this.sidenavService.isPhone()) {
			void this.sidenavService.openRightPanel();
		}

		this.keyManager = new ActiveDescendantKeyManager(this.items).withWrap().withTypeAhead();
	}

	@HostListener('window:keydown.control.alt.t', ['$event'])
	doToggleMenu(event: KeyboardEvent) {
		event.preventDefault();
		const element = document.getElementById('toggle');
		element.click();
	}

	onKeydown(event: any) {
		if (event.keyCode === 13) {
			this.menuService.reportSelect = this.keyManager.activeItem.item.report;
			this.selectReport(this.keyManager.activeItem.item.report, true);
		} else {
			this.keyManager.onKeydown(event);
		}
	}

	selectReport(report: IReport | null, navigate: boolean = false) {
		this.reportService.report = report;
		if (navigate) {
			void this.router.navigate(['insights/reports']);
		}
	}

	itemSelected(report: IReport) {
		this.keyManager.setActiveItem(this.items.find((x) => x.getLabel() === report.name));
		if (
			!this.menuService.reportSelect ||
			this.menuService.reportSelect.reportId !== report.reportId ||
			this.menuService.reportSelect.id !== report.id ||
			!this.router.isActive('insights/reports', {
				paths: 'subset',
				queryParams: 'subset',
				fragment: 'ignored',
				matrixParams: 'ignored',
			})
		) {
			this.menuService.reportSelect = report;
			this.selectReport(report, true);
			if (this.sidenavService.isPhone()) {
				void this.sidenavService.closeRightPanel();
			}
		}
	}

	exitImpersonation() {
		this.insightsService.exitImpersonation(this.permissions);
	}

	activateSlideshow() {
		console.log('activating slideshow');
		void this.router.navigate(['insights/slideshow']);
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}
}
